import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
    />
  );
}

export default function DialogCampanhasAlertasEditar({
  PaperComponent,
  open,
  onClose,
  alertas,
  loading,
  limparVendedor,
  gravarResolucaoAlerta,
  recarregarAlertas,
}) {
  const [vendedorSelecionado, setVendedorSelecionado] = useState("");
  const [filtroResolucao, setFiltroResolucao] = useState("TODOS");

  const [alertasLocais, setAlertasLocais] = useState([]);

  useEffect(() => {
    setAlertasLocais(Array.isArray(alertas) ? alertas : []);
  }, [alertas]);

  const vendedores = useMemo(() => {
    const lista = (Array.isArray(alertasLocais) ? alertasLocais : [])
      .map((a) => limparVendedor(a.vendedor) || a.refvendedor || "SEM VENDEDOR")
      .filter(Boolean);

    return [...new Set(lista)].sort((a, b) => a.localeCompare(b));
  }, [alertasLocais, limparVendedor]);

  const vendedorAtivo = vendedorSelecionado || vendedores[0] || "";

  const dados = useMemo(() => {
    return (Array.isArray(alertasLocais) ? alertasLocais : []).filter((a) => {
      const vend = limparVendedor(a.vendedor) || a.refvendedor || "SEM VENDEDOR";

      if (vendedorAtivo && vend !== vendedorAtivo) return false;

      if (filtroResolucao === "TRATADOS") {
        return String(a.obsresolucao || "").trim() !== "";
      }

      if (filtroResolucao === "POR_TRATAR") {
        return String(a.obsresolucao || "").trim() === "";
      }

      return true;
    });
  }, [alertasLocais, vendedorAtivo, filtroResolucao, limparVendedor]);

  const columns = useMemo(
    () => [
      { Header: "Campanha", accessor: "campanha", Filter: TextFilter },
      { Header: "Código", accessor: "codigo_campanha", Filter: TextFilter },
      { Header: "Fatura", accessor: "faturaorigem", Filter: TextFilter },
      { Header: "Data", accessor: "faturadata", Filter: TextFilter },
      { Header: "Cliente", accessor: "cliente", Filter: TextFilter },
      { Header: "Tipo alerta", accessor: "tipoalerta", Filter: TextFilter },
      { Header: "Detalhe", accessor: "detalhe", Filter: TextFilter },
      {
        Header: "Resolução",
        accessor: "obsresolucao",
        Filter: TextFilter,
        Cell: ({ row }) => {
          const a = row.original;

          return (
            <textarea
              key={`obs-${a.id}`}
              value={a.obsresolucao || ""}
              onChange={(e) => {
                const novoValor = e.target.value;

                setAlertasLocais((prev) =>
                  prev.map((x) => (x.id === a.id ? { ...x, obsresolucao: novoValor } : x))
                );
              }}
              onBlur={async (e) => {
                const novoValor = e.target.value;

                if (true) {
                  await gravarResolucaoAlerta(a, novoValor);

                  setAlertasLocais((prev) =>
                    prev.map((x) =>
                      x.id === a.id
                        ? {
                            ...x,
                            obsresolucao: novoValor,
                            estado_resolucao: String(novoValor || "").trim()
                              ? "Tratado"
                              : "Por tratar",
                          }
                        : x
                    )
                  );
                }
              }}
              rows={2}
              style={{
                width: "100%",
                minWidth: 280,
                resize: "vertical",
                fontSize: 12,
                border: "1px solid #d2d6da",
                borderRadius: 6,
                padding: 6,
                color: "#344767",
              }}
            />
          );
        },
      },
      {
        Header: "Estado resolução",
        accessor: "estado_resolucao",
        Filter: TextFilter,
        Cell: ({ row }) =>
          String(row.original.obsresolucao || "").trim() ? "Tratado" : "Por tratar",
      },
      { Header: "Alterado por", accessor: "obsresolucao_por_nome", Filter: TextFilter },
      { Header: "Alterado em", accessor: "obsresolucao_em", Filter: TextFilter },
    ],
    [gravarResolucaoAlerta, recarregarAlertas]
  );

  const table = useTable(
    {
      columns,
      data: dados,
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = table;

  return (
    <Dialog
      PaperComponent={PaperComponent}
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 24px)", xl: "1400px" },
          maxWidth: "100%",
          m: { xs: 1.5, md: 3 },
        },
      }}
    >
      <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
        Editar alertas
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 1.5, md: 3 },
          maxWidth: "100%",
          overflowX: "hidden",
        }}
      >
        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="260px">
            <CircularProgress />
          </MDBox>
        ) : (
          <>
            <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1.5}>
              <MDBox sx={{ maxWidth: "100%", overflowX: "auto", flex: 1 }}>
                <Tabs
                  value={vendedorAtivo}
                  onChange={(_, value) => setVendedorSelecionado(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {vendedores.map((v) => (
                    <Tab key={v} value={v} label={v} />
                  ))}
                </Tabs>
              </MDBox>

              <select
                value={filtroResolucao}
                onChange={(e) => setFiltroResolucao(e.target.value)}
                style={{
                  padding: "8px",
                  borderRadius: 8,
                  border: "1px solid #d2d6da",
                  fontSize: 13,
                  color: "#344767",
                }}
              >
                <option value="TODOS">Todos</option>
                <option value="POR_TRATAR">Por tratar</option>
                <option value="TRATADOS">Tratados</option>
              </select>
            </MDBox>

            <MDBox
              sx={{
                maxHeight: { xs: "62vh", md: "65vh" },
                overflow: "auto",
                maxWidth: "100%",
              }}
            >
              <table
                {...getTableProps()}
                style={{
                  width: "100%",
                  minWidth: 1300,
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()}>
                      {hg.headers.map((col) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 3,
                            background: "#fff",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                            fontSize: 12,
                            padding: 6,
                            textAlign: "left",
                            verticalAlign: "bottom",
                            minWidth:
                              col.id === "detalhe"
                                ? 260
                                : col.id === "obsresolucao"
                                ? 300
                                : undefined,
                          }}
                        >
                          <div>
                            {col.render("Header")}
                            {col.isSorted ? (col.isSortedDesc ? " ▼" : " ▲") : null}
                          </div>

                          {!col.disableFilters && (
                            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                              {col.canFilter ? col.render("Filter") : null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={11}
                        style={{
                          fontSize: 13,
                          padding: 18,
                          textAlign: "center",
                          color: "#7b809a",
                          borderTop: "1px solid #eee",
                        }}
                      >
                        Sem alertas para o filtro selecionado.
                      </td>
                    </tr>
                  )}

                  {rows.map((row) => {
                    prepareRow(row);

                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            style={{
                              fontSize: 12,
                              padding: 6,
                              borderTop: "1px solid #eee",
                              color: "#1a2b4c",
                              verticalAlign: "top",
                              minWidth:
                                cell.column.id === "detalhe"
                                  ? 260
                                  : cell.column.id === "obsresolucao"
                                  ? 300
                                  : undefined,
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexWrap: "wrap",
          gap: 1,
          px: { xs: 2, md: 3 },
          pb: { xs: 2, md: 2 },
          "& .MuiButton-root": {
            width: { xs: "100%", sm: "auto" },
          },
        }}
      >
        <MDButton color="secondary" onClick={onClose}>
          Fechar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
